// src/context/PermissionContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { apiCall } from "../api/apiCall";
import { getAccessToken, clearSession } from "../utils/authSession";
import { useNavigate } from "react-router-dom";

const PermissionContext = createContext(undefined);

// current_status ab backend se full country/city/ops tree "assigned" flags ke
// saath aata hai — isko flat "COUNTRY", "COUNTRY::CITY", "COUNTRY::CITY::OPS"
// path strings mein convert karte hain (independent markers, koi cascade nahi).
const parseCurrentStatusTree = (raw = []) => {
  if (!Array.isArray(raw)) return [];
  if (raw.length > 0 && typeof raw[0] === "string") return raw;

  const paths = [];
  raw.forEach((country) => {
    if (country?.assigned) paths.push(country.country_name);
    (country?.cities ?? []).forEach((city) => {
      if (city?.assigned) paths.push(`${country.country_name}::${city.city_name}`);
      (city?.ops_list ?? []).forEach((op) => {
        if (op?.assigned) paths.push(`${country.country_name}::${city.city_name}::${op.ops}`);
      });
    });
  });
  return paths;
};

export const PermissionProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [permissions, setPermissions] = useState(() => new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedPermissions, setHasLoadedPermissions] = useState(false);
  const [userContracts, setUserContracts] = useState(null); // ← NEW
  const [allMYContracts, setAllMYContracts] = useState(null); // ← NEW

  const hasFetchedRef = useRef(false);
  const navigate = useNavigate();

  // ── NEW: Fetch contracts assigned to logged-in user ──────────────────────
  const fetchUserContracts = useCallback(async () => {
    const rawUser = localStorage.getItem("user");
    if (!rawUser) return;

    let userId;
    try {
      userId = JSON.parse(rawUser)?.user_id;
    } catch {
      return;
    }

    if (!userId) return;

    try {
      const res = await apiCall("GET", `/access-control/user/${userId}`, null, null);
      if (res?.success && res?.data?.permissions) {
        const BOARDPACK_MAP = {
          "ALL COMPANIES": "ALL COMPANIES",
          "Jeddah Airport Contract": "Jeddah Airport Contract",
          "Sharjah Airport Parking": "Sharjah Airport Parking",
          "Airport Jordan": "Airport Jordan",
          "Lovers Mkt Off Str-dam": "Lovers Mkt Off Str-dam",
          "Al Batha Contract": "Al Batha Contract",
          "Dammam Airport Contract": "Dammam Airport Contract",
          "TOTAL BILAL PROJECT": "TOTAL BILAL PROJECT",
          "Bab Al-Ree Contract": "Bab Al-ree Contract",
          "Gazzaz Contract": "Gazzaz Contract",
          "Jed Watch Mkt-parking": "Jed Watch Mkt-parking",
          "TOTAL DUBAI": "TOTAL DUBAI",
          "Haram Contract": "Haram Contract",
          "Taif Contract": "Taif Contract",
          "Khamis Contract": "Khamis Contract",
          "TOTAL JUBAIL": "TOTAL JUBAIL",
          "Hassa Contract": "Hassa Contract",
          "Digital City Contract": "Digital City Contract",
          "Baha Contract": "Baha Contract",
          "Dammam Causeway": "Dammam Causeway",
          "Baljurashi Contract": "Baljurashi Contract",
          "Makkah Contract": "Makkah Contract",
          "Jeddah Contract": "Jeddah Contract",
          "MOC Contract": "Moc Contract",
          "Modern City Contract": "Modern City Contract",
          "CAPITAL PARKING CO. LTD": "CAPITAL PARKING CO. LTD",
        };

        const converted = {};
        Object.keys(res.data.permissions).forEach((module) => {
          converted[module] = module === "current_status"
            ? parseCurrentStatusTree(res.data.permissions[module])
            : res.data.permissions[module].map((val) => BOARDPACK_MAP[val] ?? val);
        });
        const allConverted = {};
        Object.keys(res.data.permissions).forEach((module) => {
          allConverted[module] = module === "current_status"
            ? parseCurrentStatusTree(res.data.permissions[module])
            : res.data.permissions[module].map((val) => val);
        });
        setUserContracts(converted);
        setAllMYContracts(allConverted);
      }
    } catch (err) {
      console.error("Failed to fetch user contracts:", err);
    }
  }, []);

  const loadPermissions = useCallback(
    async (force = false) => {
      // Agar already loaded hai aur force nahi kiya to skip
      if (hasLoadedPermissions && !force) {
        setIsLoading(false);
        return;
      }

      const token = getAccessToken();
      const rawUser = localStorage.getItem("user");

      if (!token || !rawUser) {
        setIsAuthenticated(false);
        setPermissions(new Set());
        setIsLoading(false);
        setHasLoadedPermissions(false);
        return;
      }

      let roleId;
      try {
        const user = JSON.parse(rawUser);
        roleId = user?.role_id;
      } catch (error) {
        clearSession();
        setPermissions(new Set());
        setIsAuthenticated(false);
        setIsLoading(false);
        setHasLoadedPermissions(false);
        navigate("/login", { replace: true });
        return;
      }

      if (!roleId) {
        setIsAuthenticated(false);
        setPermissions(new Set());
        setIsLoading(false);
        setHasLoadedPermissions(false);
        return;
      }

      try {
        setIsLoading(true);

        const resp = await apiCall(
          "get",
          `/permissions/role/list?role_id=${roleId}`,
          null,
          navigate
        );

        if (resp?.success && Array.isArray(resp?.data)) {
          const allowedPermissions = new Set(
            resp.data.filter((p) => p.is_allowed === 1).map((p) => p.permission_key)
          );

          setPermissions(allowedPermissions);
          setIsAuthenticated(true);
          setHasLoadedPermissions(true);

          // ── NEW: permissions load hone ke baad contracts bhi fetch karo ──
          fetchUserContracts();
        } else {
          throw new Error("Invalid permission response structure");
        }
      } catch (error) {
        console.error("Permission fetch failed:", error);
        clearSession();
        setPermissions(new Set());
        setIsAuthenticated(false);
        setHasLoadedPermissions(false);
        navigate("/login", { replace: true });
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, hasLoadedPermissions, fetchUserContracts]
  );

  // Run only once on mount
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    // The render worker's page has a read-only job token and needs no permissions
    if (window.location.pathname === "/board-pack-render") {
      setIsLoading(false);
      return;
    }
    loadPermissions();
  }, [loadPermissions]);

  // Manual trigger (after login)
  const triggerPermissionLoad = useCallback(() => {
    loadPermissions(true);
  }, [loadPermissions]);

  const logout = useCallback(() => {
    clearSession();
    setPermissions(new Set());
    setIsAuthenticated(false);
    setHasLoadedPermissions(false);
    setUserContracts(null); // ← NEW: logout pe reset
    setAllMYContracts(null);
    navigate("/login", { replace: true });
  }, [navigate]);

  const hasPermission = useCallback((key) => permissions.has(key), [permissions]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      hasPermission,
      triggerPermissionLoad,
      logout,
      userContracts, // ← NEW
      allMYContracts,
      fetchUserContracts, // ← NEW
    }),
    [
      isAuthenticated,
      isLoading,
      hasPermission,
      triggerPermissionLoad,
      logout,
      userContracts,
      allMYContracts,
      fetchUserContracts,
    ]
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions must be used inside PermissionProvider");
  }
  return context;
};
